// ./ui/PreferenceForm.jsx
import React, { useMemo } from 'react';
import { Card, Form, Row, Col, InputGroup, Button, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faGlobeAmericas, faBan, faTimes, faLightbulb } from '@fortawesome/free-solid-svg-icons';
import PropTypes from 'prop-types';

const PreferenceForm = ({
    dietPreference,
    onDietPreferenceChange,
    otherDietOrCuisine,
    onOtherDietOrCuisineChange,
    avoidIngredientInput,
    onAvoidIngredientInputChange,
    ingredientsToAvoidList,
    onAddAvoidIngredient,
    onRemoveAvoidIngredient,
    customPromptText,
    onCustomPromptTextChange,
    dietAndCuisineOptions,
    userRestrictions = [], // From profile, ensure it's an array of strings or objects with a 'restriction' key
    isLoading,
    avoidInputRef,
    customPromptPlaceholder = "e.g., prefer quick meals, no spicy food" // Allow custom placeholder
}) => {

    const effectiveUserRestrictions = useMemo(() => {
        return Array.isArray(userRestrictions)
            ? userRestrictions.map(r => (typeof r === 'string' ? r.trim().toLowerCase() : (r?.restriction || '').trim().toLowerCase())).filter(Boolean)
            : [];
    }, [userRestrictions]);

    return (
        <>
            <Card className="mb-4 p-3 bg-light border shadow-sm">
                <h5 className="text-secondary mb-3">
                    <FontAwesomeIcon icon={faGlobeAmericas} className="me-2" />
                    Dietary Preferences
                </h5>
                <Row className="g-3">
                    <Col md={12}>
                        <Form.Group controlId="prefFormDietPreference">
                            <Form.Label>Diet or Cuisine Style:</Form.Label>
                            <Form.Select
                                value={dietPreference}
                                onChange={(e) => onDietPreferenceChange(e.target.value)}
                                disabled={isLoading}
                                aria-label="Select diet or cuisine style"
                            >
                                {dietAndCuisineOptions.map((opt, idx) => (
                                    opt.type === 'group'
                                        ? <optgroup key={`group-${idx}`} label={opt.label}></optgroup>
                                        : <option key={opt.value || `opt-${idx}`} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                        {dietPreference === 'other' && (
                            <Form.Group className="mt-2" controlId="prefFormOtherDiet">
                                <Form.Control
                                    type="text"
                                    placeholder="Specify other diet/cuisine"
                                    value={otherDietOrCuisine}
                                    onChange={(e) => onOtherDietOrCuisineChange(e.target.value)}
                                    disabled={isLoading}
                                />
                            </Form.Group>
                        )}
                    </Col>
                </Row>
            </Card>

            <Card className="mb-4 p-3 bg-light border shadow-sm">
                <h5 className="text-secondary mb-3">
                    <FontAwesomeIcon icon={faBan} className="me-2" />
                    Restrictions & Custom Instructions
                </h5>
                <Form.Group controlId="prefFormAvoidIngredients" className="mb-3">
                    <Form.Label>Specific Ingredients to Add to Avoid List:</Form.Label>
                    <InputGroup>
                        <Form.Control
                            ref={avoidInputRef}
                            type="text"
                            placeholder="e.g., shellfish, mushrooms"
                            value={avoidIngredientInput}
                            onChange={(e) => onAvoidIngredientInputChange(e.target.value)}
                            onKeyPress={(e) => { if (e.key === 'Enter') { e.preventDefault(); onAddAvoidIngredient(); } }}
                            disabled={isLoading}
                        />
                        <Button variant="outline-secondary" onClick={onAddAvoidIngredient} disabled={isLoading || !avoidIngredientInput.trim()}>
                            Add
                        </Button>
                    </InputGroup>
                    {ingredientsToAvoidList.length > 0 && (
                        <div className="mt-2">
                            <span className="me-2 small text-muted">Locally added to avoid:</span>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                                {ingredientsToAvoidList.map((ing, idx) => (
                                    <Badge key={`local-avoid-${idx}`} pill bg="info" text="white" className="d-flex align-items-center p-2 shadow-sm">
                                        {ing}
                                        <Button
                                            variant="link" size="sm"
                                            className="p-0 ms-1 text-white badge-remove-btn"
                                            onClick={() => onRemoveAvoidIngredient(ing)}
                                            aria-label={`Remove ${ing} from local list`} title={`Remove ${ing} from local list`}
                                            style={{ lineHeight: 1, textDecoration: 'none', verticalAlign: 'middle' }}
                                        >
                                            <FontAwesomeIcon icon={faTimes} size="xs" />
                                        </Button>
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                    {effectiveUserRestrictions.length > 0 && (
                        <div className="mt-3 pt-2 border-top">
                            <span className="me-2 small text-muted">Profile restrictions (from your settings):</span>
                            <div className="d-flex flex-wrap gap-1 mt-1">
                                {effectiveUserRestrictions.map((restriction, idx) => (
                                    <Badge key={`prop-restrict-${idx}`} pill bg="secondary" text="white" className="p-2 shadow-sm" title="This restriction is from your profile settings.">
                                        {restriction}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}
                </Form.Group>

                <Form.Group controlId="prefFormCustomPrompt">
                    <Form.Label>
                        <FontAwesomeIcon icon={faLightbulb} className="me-1" />
                        Additional Instructions for the AI:
                    </Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={2}
                        placeholder={customPromptPlaceholder}
                        value={customPromptText}
                        onChange={(e) => onCustomPromptTextChange(e.target.value)}
                        disabled={isLoading}
                    />
                </Form.Group>
            </Card>
        </>
    );
};

PreferenceForm.propTypes = {
    dietPreference: PropTypes.string.isRequired,
    onDietPreferenceChange: PropTypes.func.isRequired,
    otherDietOrCuisine: PropTypes.string.isRequired,
    onOtherDietOrCuisineChange: PropTypes.func.isRequired,
    avoidIngredientInput: PropTypes.string.isRequired,
    onAvoidIngredientInputChange: PropTypes.func.isRequired,
    ingredientsToAvoidList: PropTypes.arrayOf(PropTypes.string).isRequired,
    onAddAvoidIngredient: PropTypes.func.isRequired,
    onRemoveAvoidIngredient: PropTypes.func.isRequired,
    customPromptText: PropTypes.string.isRequired,
    onCustomPromptTextChange: PropTypes.func.isRequired,
    dietAndCuisineOptions: PropTypes.arrayOf(
        PropTypes.shape({
            value: PropTypes.string,
            label: PropTypes.string.isRequired,
            type: PropTypes.string, // for 'group'
        })
    ).isRequired,
    userRestrictions: PropTypes.arrayOf(
        PropTypes.oneOfType([
            PropTypes.string,
            PropTypes.shape({ restriction: PropTypes.string }) // Support objects with 'restriction' key
        ])
    ),
    isLoading: PropTypes.bool,
    avoidInputRef: PropTypes.oneOfType([
        PropTypes.func,
        PropTypes.shape({ current: PropTypes.instanceOf(Element) })
    ]),
    customPromptPlaceholder: PropTypes.string,
};

PreferenceForm.defaultProps = {
    userRestrictions: [],
    isLoading: false,
    avoidInputRef: null,
    customPromptPlaceholder: "e.g., prefer quick meals, make it gluten-free if possible"
};

export default PreferenceForm;